const _clusterArray = (locations: string[], masterData: any): string[] => {
  if (!locations || locations.length === 0) return [];
  
  let clusters: any[] = [];
  if (Array.isArray(masterData)) {
    clusters = masterData;
  } else if (masterData && Array.isArray(masterData.regionAndStates)) {
    clusters = masterData.regionAndStates;
  }

  if (clusters.length === 0) return locations;

  const result: string[] = [];
  const locationsSet = new Set(locations);
  const subsumedLocations = new Set<string>();

  clusters.forEach((cluster) => {
    if (cluster && cluster.cities && Array.isArray(cluster.cities) && cluster.cities.length > 0) {
      const clusterCityValues = cluster.cities.map((city: any) => city.value);
      const isCompletelySelected = clusterCityValues.every((cityVal: string) => locationsSet.has(cityVal));

      if (isCompletelySelected) {
        result.push(cluster.name);
        clusterCityValues.forEach((cityVal: string) => subsumedLocations.add(cityVal));
      }
    }
  });

  locations.forEach((loc) => {
    if (!subsumedLocations.has(loc)) {
      result.push(loc);
    }
  });

  return result;
};

const _removeFromArray = (locations: string[], removedItem: string, masterData: any): string[] => {
  if (!locations || locations.length === 0) return [];
  if (!removedItem) return locations;

  let clusters: any[] = [];
  if (Array.isArray(masterData)) {
    clusters = masterData;
  } else if (masterData && Array.isArray(masterData.regionAndStates)) {
    clusters = masterData.regionAndStates;
  }

  if (clusters.length === 0) {
    return locations.filter((loc) => loc !== removedItem);
  }

  const cluster = clusters.find((c: any) => c.name === removedItem);

  if (cluster && cluster.cities && Array.isArray(cluster.cities)) {
    const clusterCityValuesSet = new Set(cluster.cities.map((city: any) => city.value));
    return locations.filter((loc) => !clusterCityValuesSet.has(loc));
  }

  return locations.filter((loc) => loc !== removedItem);
};

/**
 * Processes a full location object, returning clustered string arrays for current and preferred locations.
 * @param locationObj - The location state object containing current_locations and preferred_locations
 * @param masterData - Master cluster data
 * @returns Object with clustered arrays: { current_locations: string[], preferred_locations: string[] }
 */
export const getClusteredLocations = (locationObj: any, masterData: any, type?: "current" | "preferred") => {
  if (!locationObj) return { current_locations: [], preferred_locations: [] };


  if(type === "current"){
    return {
      current_locations: _clusterArray(locationObj.current_locations || [], masterData),
    }
  }

  if(type === "preferred"){
    return {
      preferred_locations: _clusterArray(locationObj.preferred_locations || [], masterData)
    }
  }

  return {
    current_locations: _clusterArray(locationObj.current_locations || [], masterData),
    preferred_locations: _clusterArray(locationObj.preferred_locations || [], masterData)
  };
};

/**
 * Removes a clustered or standalone location item from a full location state object.
 * 
 * @param locationObj - The location state object
 * @param removedItem - The string to remove (either a cluster name like "Delhi NCR" or a single city)
 * @param removeType - 'current' | 'preferred' to specify which list to remove from
 * @param masterData - Master cluster data
 * @returns An object containing the fully updated locationObj, and the new clustered string arrays.
 */
export const removeLocationOrCluster = (
  locationObj: any, 
  removedItem: string, 
  removeType: 'current' | 'preferred', 
  masterData: any
) => {
  const updated = { ...locationObj };
  
  if (removeType === 'current') {
    updated.current_locations = _removeFromArray(updated.current_locations || [], removedItem, masterData);
    
    // Automatically mirror removals if preferred_locations is set to match current_locations identically
    if (updated.preferred_locations_type === 'same' || updated.includeRelocating) {
      updated.preferred_locations = [...updated.current_locations];
    }
  } else if (removeType === 'preferred') {
    updated.preferred_locations = _removeFromArray(updated.preferred_locations || [], removedItem, masterData);
  }

  const clusteredStrs = getClusteredLocations(updated, masterData);
  return {
    updatedLocations: updated,
    current_locations: clusteredStrs.current_locations,
    preferred_locations: clusteredStrs.preferred_locations
  };
};
